import { Button, CogHollowSVG, Icon } from "@newsela/angelou";

import { Assignment } from "@/types/models/Assignment.interface";
import { StreamProperties } from "@/types/models/EventProperties.interface";
import ActionsDropdown from "@/view/ActionsDropdown";
import {
  ASSIGNMENT_MODE,
  currentAssignmentMode,
} from "@/view/utils/currentAssignmentMode";
import DuplicateAssignmentOption from "./DuplicateAssignmentOption";
import DeleteAssignmentOption from "./DeleteAssignmentOption";

import {
  cogIconCss,
  dropdownButtonCss,
  dropdownItemCss,
  popOutCss,
} from "./AssignActionsDropdown.style";

interface AssignSelector {
  updatedAssignment: Pick<
    Assignment,
    "assignment_members" | "content_id" | "is_draft"
  >;
  handleAssignmentSettingsClick?: () => void;
  isMobile?: boolean | undefined;
  originalAssignment: Assignment;
  streamProperties: StreamProperties;
}

const AssignActionsDropdown = ({
  updatedAssignment,
  handleAssignmentSettingsClick,
  isMobile = false,
  originalAssignment,
  streamProperties,
}: AssignSelector) => {
  const assignmentMode = currentAssignmentMode(updatedAssignment);
  const isUpdateMode = assignmentMode === ASSIGNMENT_MODE.UPDATE;

  const assignmentSettingEventProperties = {
    objectType: "Assignment",
    actionPrefix: "Initiate",
    actionName: "Navigate",
    streamProperties: {
      ...streamProperties,
      description: "Open create assignment flow",
    },
  };

  const renderAssignmentSettingsButton = () => {
    if (updatedAssignment?.is_draft) {
      return null;
    }

    return (
      <ActionsDropdown.ButtonItem
        __cssFor={{ Button: { root: dropdownItemCss } }}
        dataProps={{
          "data-qa-selector": "assignment-dropdown-assingment-settings",
          "data-testid": "assignment-dropdown-button",
        }}
        onClick={handleAssignmentSettingsClick}
        {...assignmentSettingEventProperties}
      >
        <Icon
          isDecorative={false}
          __cssFor={{ root: cogIconCss }}
          size={Button.sizes.md}
          SvgComponent={CogHollowSVG}
          title="Assignment settings"
        />
        Assignment settings
      </ActionsDropdown.ButtonItem>
    );
  };

  const renderDuplicateAssignmentButton = () => {
    return (
      <DuplicateAssignmentOption
        updatedAssignment={updatedAssignment}
        streamProperties={streamProperties}
        originalAssignment={originalAssignment}
      />
    );
  };

  const popOutContents = () => {
    return (
      <>
        {renderAssignmentSettingsButton()}
        {renderDuplicateAssignmentButton()}
        <DeleteAssignmentOption
          streamProperties={streamProperties}
          updatedAssignment={updatedAssignment}
        />
      </>
    );
  };

  return (
    <>
      {/* @ts-expect-error Not all aria-prop properties are required */}
      <ActionsDropdown
        ariaProps={{ "aria-label": "View assignment options." }}
        buttonCss={dropdownButtonCss(isMobile, isUpdateMode)}
        buttonFlavor={Button.flavors.primary}
        dataProps={{ "data-qa-selector": "initiate-assignment-update" }}
        horizontalPopOutPosition="RIGHT"
        id="assignment-options-dropdown"
        popOutCss={{ root: popOutCss }}
        renderDropdownItems={popOutContents}
      />
    </>
  );
};

export default AssignActionsDropdown;
